import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SectionCard } from '@/components/dashboard/section-card'
import { FormField, fieldMessageId } from '@/components/dashboard/form-field'
import { WORKSPACE } from '@/data/team'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

type WorkspaceValues = {
    name: string
    slug: string
    timezone: string
    fiscalStart: string
    dueDays: string
}

type WorkspaceErrors = Partial<Record<keyof WorkspaceValues, string>>

const TIMEZONES = [
    { value: 'Asia/Jakarta', label: 'WIB, Jakarta (UTC+7)' },
    { value: 'Asia/Makassar', label: 'WITA, Makassar (UTC+8)' },
    { value: 'Asia/Jayapura', label: 'WIT, Jayapura (UTC+9)' }
]

const FISCAL_MONTHS = [
    { value: '1', label: 'Januari' },
    { value: '4', label: 'April' },
    { value: '7', label: 'Juli' },
    { value: '10', label: 'Oktober' }
]

const INITIAL: WorkspaceValues = {
    name: WORKSPACE.name,
    slug: WORKSPACE.slug,
    timezone: 'Asia/Jakarta',
    fiscalStart: '1',
    dueDays: '7'
}

const KEYS: (keyof WorkspaceValues)[] = ['name', 'slug', 'timezone', 'fiscalStart', 'dueDays']

const validate = (values: WorkspaceValues): WorkspaceErrors => {
    const errors: WorkspaceErrors = {}
    const days = Number(values.dueDays)

    if (values.name.trim().length < 2) errors.name = 'Nama workspace minimal 2 karakter.'
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(values.slug)) errors.slug = 'Gunakan huruf kecil, angka, dan tanda hubung tanpa spasi.'
    else if (values.slug.length < 3 || values.slug.length > 30) errors.slug = 'Alamat terdiri dari 3 sampai 30 karakter.'
    if (!Number.isInteger(days) || days < 1 || days > 60) errors.dueDays = 'Isi antara 1 sampai 60 hari.'

    return errors
}

export const WorkspaceForm: FC = (): ReactNode => {
    const [saved, setSaved] = useState<WorkspaceValues>(INITIAL)
    const [values, setValues] = useState<WorkspaceValues>(INITIAL)
    const [errors, setErrors] = useState<WorkspaceErrors>({})
    const dirty = KEYS.some((key) => values[key] !== saved[key])

    const update = (key: keyof WorkspaceValues, value: string): void => {
        setValues((current) => ({ ...current, [key]: value }))
        if (errors[key]) setErrors((current) => ({ ...current, [key]: undefined }))
    }

    const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        const nextErrors = validate(values)
        setErrors(nextErrors)

        const firstInvalid = KEYS.find((key) => nextErrors[key])

        if (firstInvalid) {
            document.getElementById(`workspace-${firstInvalid}`)?.focus()
            return
        }

        const trimmed = { ...values, name: values.name.trim() }
        setValues(trimmed)
        setSaved(trimmed)
        toast.success('Pengaturan workspace disimpan')
    }

    return (
        <form onSubmit={onSubmit} noValidate>
            <SectionCard
                title='Workspace'
                description='Identitas workspace dan aturan penagihan yang dipakai di seluruh dasbor.'
                footer={(
                    <div className='flex flex-wrap items-center justify-end gap-2'>
                        <Button
                            type='button'
                            variant='ghost'
                            disabled={!dirty}
                            onClick={() => {
                                setValues(saved)
                                setErrors({})
                            }}
                        >
                            Batalkan
                        </Button>
                        <Button type='submit' disabled={!dirty}>
                            Simpan perubahan
                        </Button>
                    </div>
                )}
            >
                <div className='grid gap-4 sm:grid-cols-2'>
                    <FormField id='workspace-name' label='Nama workspace' required error={errors.name}>
                        <Input
                            id='workspace-name'
                            value={values.name}
                            onChange={(event) => update('name', event.target.value)}
                            aria-invalid={Boolean(errors.name)}
                            aria-describedby={errors.name ? fieldMessageId('workspace-name') : undefined}
                        />
                    </FormField>

                    <FormField
                        id='workspace-slug'
                        label='Alamat workspace'
                        required
                        error={errors.slug}
                        hint={`Dasbor bisa dibuka di app.arunika.id/${values.slug || 'nama-workspace'}`}
                    >
                        <div className='flex h-8 items-center overflow-hidden rounded-lg border border-input focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 dark:bg-input/30'>
                            <span className='flex h-full items-center border-r bg-muted px-2.5 text-sm text-muted-foreground'>
                                app.arunika.id/
                            </span>
                            <input
                                id='workspace-slug'
                                value={values.slug}
                                onChange={(event) => update('slug', event.target.value.toLowerCase())}
                                className='h-full min-w-0 flex-1 bg-transparent px-2.5 text-sm outline-none'
                                aria-invalid={Boolean(errors.slug)}
                                aria-describedby={fieldMessageId('workspace-slug')}
                            />
                        </div>
                    </FormField>

                    <FormField id='workspace-timezone' label='Zona waktu laporan'>
                        <Select
                            items={TIMEZONES}
                            value={values.timezone}
                            onValueChange={(value) => {
                                if (value) update('timezone', value as string)
                            }}
                        >
                            <SelectTrigger id='workspace-timezone' className='w-full'>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {TIMEZONES.map((item) => (
                                    <SelectItem key={item.value} value={item.value}>
                                        {item.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </FormField>

                    <FormField id='workspace-fiscalStart' label='Awal tahun fiskal'>
                        <Select
                            items={FISCAL_MONTHS}
                            value={values.fiscalStart}
                            onValueChange={(value) => {
                                if (value) update('fiscalStart', value as string)
                            }}
                        >
                            <SelectTrigger id='workspace-fiscalStart' className='w-full'>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {FISCAL_MONTHS.map((item) => (
                                    <SelectItem key={item.value} value={item.value}>
                                        {item.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </FormField>

                    <FormField id='workspace-dueDays' label='Jatuh tempo faktur (hari)' required error={errors.dueDays} hint='Dihitung sejak faktur diterbitkan.'>
                        <Input
                            id='workspace-dueDays'
                            type='number'
                            inputMode='numeric'
                            min={1}
                            max={60}
                            value={values.dueDays}
                            onChange={(event) => update('dueDays', event.target.value)}
                            aria-invalid={Boolean(errors.dueDays)}
                            aria-describedby={fieldMessageId('workspace-dueDays')}
                        />
                    </FormField>

                    <FormField id='workspace-currency' label='Mata uang' hint='Semua nominal ditampilkan dalam Rupiah.'>
                        <Input id='workspace-currency' value='IDR, Rupiah Indonesia' disabled aria-describedby={fieldMessageId('workspace-currency')} />
                    </FormField>
                </div>
            </SectionCard>
        </form>
    )
}
